// Must match apps/customers/lib/locationSlug.js + apps/backend/functions/utilities/locationSlug.js.
export const slugify = (value) =>
    String(value || "")
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/['‘’]/g, "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

const stripBrandPrefix = (slug, brandName) => {
    if (!slug) return slug;
    const brandSlug = slugify(brandName);
    if (!brandSlug) return slug;
    const prefix = `${brandSlug}-`;
    return slug.startsWith(prefix) ? slug.slice(prefix.length) : slug;
};

// Only Open and Referral locations have customer-facing landing pages.
// Closed, Non Participating, Temporarily Closed, Scheduled are not served.
export const LOCATION_URL_STATUSES = new Set(["Open", "Referral"]);

export const isLocationUrlEligible = (location) =>
    !!location && LOCATION_URL_STATUSES.has(location.status);

// The customers app serves location pages at /locations/{location-slug}/, where
// the brand prefix is implicit per-deploy. The canonical `slug` is computed
// by the backend (apps/backend/functions/cron/locationsDerive.f.js) — it
// carries uniqueness suffixes (e.g. `tucson-2`) and the brand+location naming
// the customer site expects — and is indexed onto each Algolia location hit
// by utilities/algolia/locations.js#algoliaLocationUpsert. Prefer that stored
// value; fall back to a local recomputation from `location.location` for any
// Algolia record that has not been re-indexed yet (the field rolls out on
// natural location-edit triggers and can be backfilled by calling the
// `requeueAlgolia({collection: "locations"})` admin callable).
// `includeBrandInLocationUrl` is sourced from the account holder's user
// record — true forces a `{brand}-{location}` slug (prepending when the
// source slug lacks the prefix); false/missing strips any leading brand
// prefix so the URL is just the location portion.
export const buildLocationUrl = (siteUrl, brandName, location, includeBrandInLocationUrl) => {
    if (!siteUrl || !location) return null;
    if (!isLocationUrlEligible(location)) return null;
    const rawSlug = location.slug || slugify(location.location);
    if (!rawSlug) return null;
    const brandSlug = slugify(brandName);
    const hasBrandPrefix = brandSlug ? rawSlug.startsWith(`${brandSlug}-`) : false;
    let urlSlug;
    if (includeBrandInLocationUrl) {
        urlSlug = hasBrandPrefix || !brandSlug ? rawSlug : `${brandSlug}-${rawSlug}`;
    } else {
        urlSlug = stripBrandPrefix(rawSlug, brandName);
    }
    if (!urlSlug) return null;
    return `${siteUrl.replace(/\/+$/, "")}/locations/${urlSlug}/`;
};
